import React, { useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { useQueryClient } from "@tanstack/react-query";

import {
  MODULE_KIND_DISPLAY_NAMES,
  ModuleKind,
} from "src/adminPanel/modulesV2/constants";
import { moduleKeys } from "src/api/modulesApi";
import { useCreateModule } from "src/api/workspacesV2Api";
import { Button } from "src/base-components/Button";
import { Combobox } from "src/base-components/Combobox";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";

type Workspace = {
  id: string;
  name: string;
  slug: string;
  organization_id: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  afterLeave?: () => void;
  moduleKind: ModuleKind;
  workspaces: Workspace[];
  existingWorkspaceIds: Set<string>;
  orgMap: Map<string, string>;
};

type WorkspaceOption = {
  key: string;
  value: React.ReactNode;
  searchText: string;
  workspace: Workspace;
};

export const EnableModuleModal: React.FC<Props> = ({
  isOpen,
  onClose,
  afterLeave,
  moduleKind,
  workspaces,
  existingWorkspaceIds,
  orgMap,
}) => {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>("");
  const queryClient = useQueryClient();
  const createModule = useCreateModule();

  const displayName = MODULE_KIND_DISPLAY_NAMES[moduleKind];

  const availableWorkspaces = workspaces.filter(
    (ws) => !existingWorkspaceIds.has(ws.id),
  );

  const workspaceOptions: WorkspaceOption[] = availableWorkspaces.map((ws) => {
    const orgName = orgMap.get(ws.organization_id) ?? ws.organization_id;

    return {
      key: ws.id,
      value: (
        <>
          <div className="font-inter-semibold-12px">{ws.name}</div>
          <div className="font-inter-normal-12px text-gray-500">{ws.slug}</div>
          <div className="font-inter-normal-12px text-gray-400">{orgName}</div>
        </>
      ),
      searchText: `${ws.name} ${ws.slug} ${ws.id} ${ws.organization_id} ${orgName}`,
      workspace: ws,
    };
  });

  const handleCreate = async () => {
    if (!selectedWorkspaceId) return;

    const selectedWorkspace = availableWorkspaces.find(
      (ws) => ws.id === selectedWorkspaceId,
    );
    if (!selectedWorkspace) return;

    try {
      await createModule.mutateAsync({
        workspaceId: selectedWorkspaceId,
        moduleType: moduleKind,
      });

      await queryClient.invalidateQueries({ queryKey: moduleKeys.all() });

      toastActions.success({
        title: "Module enabled successfully",
        description: `${displayName} module has been enabled for ${selectedWorkspace.name}`,
      });

      handleClose();
    } catch (error) {
      logger.error("Failed to enable module", error);
      toastActions.failure({
        title: "Failed to enable module",
        description: "Please try again or start a thread in #team-platform",
      });
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedWorkspaceId("");
  };

  const selectedWorkspace = availableWorkspaces.find(
    (ws) => ws.id === selectedWorkspaceId,
  );

  return (
    <Modal
      afterLeave={afterLeave}
      open={isOpen}
      size="md"
      onClose={handleClose}
    >
      <Modal.Header>Enable {displayName} Module</Modal.Header>
      <Modal.Content>
        <div className="mb-20 space-y-4">
          <p className="font-inter-normal-13px text-gray-600">
            Select a workspace to enable the{" "}
            <span className="font-inter-semibold-13px">
              {displayName.toLowerCase()}
            </span>{" "}
            module for. Only workspaces that don&apos;t already have this module
            are shown.
          </p>

          <div className="space-y-2">
            <label className="block font-inter-semibold-13px text-gray-700">
              Select Workspace
            </label>
            <Combobox
              dropdownPlaceholder={
                <div className="px-4 py-8 text-center font-inter-normal-13px text-gray-500">
                  {availableWorkspaces.length === 0
                    ? "All workspaces already have this module enabled"
                    : "No workspaces match your search"}
                </div>
              }
              options={workspaceOptions}
              placeholder="Search by workspace name, slug, ID, or organization..."
              value={selectedWorkspaceId}
              onChange={(value) => setSelectedWorkspaceId(value || "")}
            />
          </div>

          {selectedWorkspace && (
            <div className="rounded-lg border bg-gray-50 p-4">
              <h3 className="mb-4 font-inter-semibold-13px text-gray-900">
                Selected Workspace Details
              </h3>

              <div className="grid grid-cols-1 gap-4 font-inter-normal-13px">
                <div className="space-y-3">
                  <div>
                    <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                      Organization
                    </div>
                    <a
                      className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      href={`/admin-panel/organizations?search=${selectedWorkspace.organization_id}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {orgMap.get(selectedWorkspace.organization_id) ??
                        selectedWorkspace.organization_id}
                    </a>
                    <div className="font-code-12 text-gray-500">
                      ID: {selectedWorkspace.organization_id}
                    </div>
                  </div>

                  <div>
                    <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                      Workspace
                    </div>
                    <a
                      className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      href={`/admin-panel/organizations?search=${selectedWorkspace.id}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {selectedWorkspace.name}
                    </a>
                    <div className="font-code-12 text-gray-500">
                      Slug: {selectedWorkspace.slug}
                    </div>
                    <div className="font-code-12 text-gray-500">
                      ID: {selectedWorkspace.id}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button
            disabled={!selectedWorkspaceId || createModule.isPending}
            iconLeft={faPlus}
            loading={createModule.isPending}
            variant="primary"
            onClick={handleCreate}
          >
            {createModule.isPending ? "Enabling..." : "Enable Module"}
          </Button>
        }
        secondaryButton="Cancel"
      />
    </Modal>
  );
};
